/* eslint-disable react-refresh/only-export-components */
// DO NOT RENAME TO .TS file breaks process.env
import { Extender, Link, Text, Code, Heading } from "@amzn/storm-ui";
import React from "react";
import * as R from "remeda";
import StackTracey from "stacktracey";

import type { RequestParameters, Comparator, Filter } from "../types/common";

/**
 * Converts key, condition, value into format used by endpoints to filter getParameters
 */
export const buildQueryParameterFilter = (
    key: string,
    comparator: Comparator,
    value: string | string[],
    type?: string,
): Filter => {
    const filter: Filter = {
        key,
        comparator,
    };
    if (Array.isArray(value)) {
        filter.values = value;
    } else {
        filter.value = value;
    }

    if (type) {
        filter.type = type;
    }

    return filter;
};

/**
 * This is quite a complicated type to figure out, as its super generic will fix later
 * TODO
 * @param map with key:value where key is dimension data key value and value is requestParameter Key
 * Example: {value: 'test', name: 'label'} with a map of {value: 'requestParameterValue', name: 'requestParameterName'}
 * will make an output of {requestParameterValue: 'test', requestParameterName: 'label'}
 */
export const buildRequestParameter = (
    map: any,
): ((value: any) => RequestParameters) => {
    return (value: any) =>
        R.pipe(
            map,
            R.keys,
            R.filter((e: any) => R.keys(value).includes(e)),
            R.map((e: any) => ({
                [map[e]]: getStringValueForRequestParameter(value[e]),
            })),
            R.mergeAll,
        ) as RequestParameters;
};

export const getStringValueForRequestParameter = (e: any) => {
    switch (typeof e) {
        case "string":
            return e;
        case "number":
            return e.toString();
        case "object":
            return JSON.stringify(e);
        case "boolean":
            return JSON.stringify(e);
        default:
            return "";
    }
};

export const prettyPrintParameters = (parameters: object): string[] => {
    const output: string[] = [];
    Object.entries(parameters)
        .sort()
        .forEach(([key, value]) => {
            output.push(`${key}: ${value}`);
        });
    return output;
};

export const useGetComponentHeights = (selectors?: string[]) => {
    const atcSelectors = ["console-header-section", "footer"];
    const componentSelectors = selectors ?? [];
    const heights = R.pipe(
        [...componentSelectors, ...atcSelectors],
        R.map((e) => document.getElementById(e)),
        R.filter(R.isNot(R.isNil)),
        R.map((e) => e.offsetHeight),
    );
    return React.useMemo(() => {
        return R.reduce(heights, (acc, x) => acc + x, 0);
    }, [...heights]);
};

export interface ErrorPromptDetailsProps {
    title?: string;
    externalLink?: string;
    externalLinkTitle?: string;
    error: Error;
}
export const ErrorPromptDetails = (
    props: ErrorPromptDetailsProps,
): JSX.Element => {
    const { error, title, externalLink, externalLinkTitle } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const errorMessage = error.message || error.toString();
    return (
        <div>
            <Heading renderAs="h3">{title || "An error has occured"}</Heading>
            {externalLink && (
                <Text>
                    <Link
                        size="xsmall"
                        target="_blank"
                        external
                        href={externalLink}
                    >
                        {externalLinkTitle || "Troubleshooting Guide"}
                    </Link>
                </Text>
            )}
            <Code renderAs="code">{errorMessage}</Code>
            <Extender
                label={isOpen ? "Hide Details" : "Show Details"}
                handleClick={() => setIsOpen(!isOpen)}
                isOpen={isOpen}
            >
                <pre style={{ overflow: "scroll" }}>
                    {new StackTracey(error).withSources().asTable()}
                </pre>
            </Extender>
        </div>
    );
};
